import React, { useMemo } from 'react';
import { Card, Popconfirm } from 'antd';
import { DeleteOutlineRounded, ModeOutlined, Settings } from '@mui/icons-material';
import { VoiceProvider } from 'src/enums/EVoiceProvider';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import { VoiceLibraryCardProps, ProviderType, LanguageType } from '@/interfaces/IVoice';
import { voiceModelOptions } from 'src/constants/voiceModels';
import AudioPlayer from './AudioPlayer';
import { VoiceConfigModal } from './voiceConfigModal';
import VoiceInfo from './voiceInfo';
import { useVoiceConfig } from 'src/Hooks/useVoiceConfig';

const VoiceLibraryCard: React.FC<VoiceLibraryCardProps> = ({
	index,
	data,
	isSelected,
	englishVoiceConfig,
	otherVoiceConfig,
	onApplyClick,
	handleApplyClick,
	currentlyPlayingIndex,
	onPlayClick,
	user,
	deleteVoiceMutation,
	voiceModalCredits,
	fromConfig,
}) => {
	const [open, setOpen] = React.useState(false);
	const loggedInUser = useAppSelector(getLoggedInUser);

	const {
		voiceModel,
		setVoiceModel,
		speed,
		setSpeed,
		speedRime,
		setSpeedRime,
		emotion,
		setEmotion,
		level,
		setLevel,
		accent,
		setAccent,
		emotionalRange,
		setEmotionalRange,
		intonation,
		setIntonation,
		impression,
		setImpression,
		tone,
		setTone,
		whispering,
		setWhispering,
		voicePrompt,
		setVoicePrompt,
		customPauses,
		setCustomPauses,
		customPronunciation,
		setCustomPronunciation,
		temperature,
		setTemperature,
	} = useVoiceConfig(data, englishVoiceConfig, otherVoiceConfig);

	const filteredVoiceModels = useMemo(() => {
		if (!data.provider || !data.language) {
			return [];
		}
		let modelOptions =
			voiceModelOptions[data.provider as ProviderType]?.[data.language as LanguageType] || [];
		if (data.provider === VoiceProvider.RIME && data.language === 'English' && data.model) {
			modelOptions = [{ value: data.model, label: data.model }];
		}
		return modelOptions;
	}, [data.provider, data.language]);

	const handleSelected = () => {
		if (!isSelected && !currentlyPlayingIndex) {
			onApplyClick(data.voiceId);
		}
	};

	const handleOk = async () => {
		const voiceConfig = {
			speed:
				data.provider === VoiceProvider.RIME && data.model === 'mistv2'
					? speedRime.toString()
					: speed,
			emotion,
			level,
			voiceModel,
			...(data.provider === VoiceProvider.OpenAI && {
				accent,
				emotionalRange,
				intonation,
				impression,
				tone,
				whispering,
				voicePrompt,
			}),
			...(data.provider === VoiceProvider.RIME &&
				data.model === 'arcana' && {
					temperature,
				}),
			...(data.provider === VoiceProvider.RIME &&
				data.model === 'mistv2' && {
					customPauses,
					customPronunciation,
				}),
		};
		handleApplyClick(data.voiceId, data.voiceId, data.provider, voiceConfig);
		setOpen(false);
	};

	const handleSettingsClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		setOpen(true);
	};

	const handleDelete = (e?: React.MouseEvent<HTMLElement>) => {
		if (e) {
			e.stopPropagation();
		}
		deleteVoiceMutation({ voiceId: data.voiceId, fileName: data.fileName });
	};

	return (
		<>
			<Card
				className={`relative flex flex-col w-70 border-[2px] rounded-xl gap-4 p-0 group my-1 
        ${currentlyPlayingIndex !== null && currentlyPlayingIndex !== index ? 'opacity-50 cursor-not-allowed' : ''}
        ${isSelected ? 'border-blue-500' : 'border-gray-300'}
        hover:shadow-lg transition-all duration-300 bg-white`}
				data-testid={`${data?.voiceId}`}
				bodyStyle={{ padding: 10 }}
			>
				<VoiceInfo
					data={data}
					loggedInUser={loggedInUser}
					isSelected={isSelected}
					voiceModalCredits={voiceModalCredits}
					fromConfig={fromConfig}
				/>

				<div className="mt-4">
					<AudioPlayer
						fileName={data.fileName}
						onPlay={() => onPlayClick(index)}
						onPause={() => onPlayClick(null)}
						currentlyPlayingIndex={currentlyPlayingIndex}
						index={index}
						isSelected={isSelected}
						handleSelected={handleSelected}
					/>
				</div>

				<div className="absolute top-2 right-1 flex gap-2">
					{isSelected &&
						(data.provider === VoiceProvider.Cartesia ||
							data.provider === VoiceProvider.ElevenLabs ||
							data.provider === VoiceProvider.OpenAI ||
							data.provider === VoiceProvider.RIME) && (
							<div
								onClick={handleSettingsClick}
								className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								<Settings
									data-testid="settings-test-button"
									className="text-blue-500 cursor-pointer rounded-full p-1.5 bg-blue-50 hover:bg-blue-100"
									fontSize="large"
								/>
							</div>
						)}
					{user?.isSuperAdmin && (
						<>
							{!data.isCloned && (
								<div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
									<ModeOutlined
										fontSize="medium"
										className="text-purple-500 cursor-pointer rounded-full p-1.5 bg-purple-50 hover:bg-purple-100"
									/>
								</div>
							)}
							<Popconfirm
								title="Are you sure to delete this voice?"
								onConfirm={handleDelete}
								okText="Yes"
								cancelText="No"
								placement="left"
							>
								<div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
									<DeleteOutlineRounded
										fontSize="medium"
										className="text-red-500 cursor-pointer rounded-full p-1.5 bg-red-50 hover:bg-red-100"
									/>
								</div>
							</Popconfirm>
						</>
					)}
				</div>
			</Card>
			<VoiceConfigModal
				open={open}
				onCancel={() => setOpen(false)}
				onOk={handleOk}
				data={data}
				voiceModel={voiceModel}
				setVoiceModel={setVoiceModel}
				filteredVoiceModels={filteredVoiceModels}
				speed={speed}
				setSpeed={setSpeed}
				speedRime={speedRime}
				setSpeedRime={setSpeedRime}
				emotion={emotion}
				setEmotion={setEmotion}
				level={level}
				setLevel={setLevel}
				accent={accent}
				setAccent={setAccent}
				emotionalRange={emotionalRange}
				setEmotionalRange={setEmotionalRange}
				intonation={intonation}
				setIntonation={setIntonation}
				impression={impression}
				setImpression={setImpression}
				tone={tone}
				setTone={setTone}
				whispering={whispering}
				setWhispering={setWhispering}
				voicePrompt={voicePrompt}
				setVoicePrompt={setVoicePrompt}
				customPauses={customPauses}
				setCustomPauses={setCustomPauses}
				customPronunciation={customPronunciation}
				setCustomPronunciation={setCustomPronunciation}
				temperature={temperature}
				setTemperature={setTemperature}
			/>
		</>
	);
};

export default VoiceLibraryCard;
